import { useCallback, useEffect, useState } from 'react';
import { MENU_GRUPLARI } from './menu';
import type { MenuGrupAnahtari } from './menu';

/**
 * MENÜDE AÇIK GRUP (sol menü gruplama, 29.09.2026 — Kapsam §20.3a T4 ✏️ ·
 * §14 MG-6; `.claude/rules/gorsel-dil.md` §13).
 *
 * ✏️ **AYNI GÜN REVİZE (ürün sahibi 29.09.2026 — MG-6): AYNI ANDA TEK GRUP
 * AÇIKTIR (AKORDEON).** *İlk yazımda gruplar birbirinden bağımsız açılıp
 * kapanıyordu ve açılan grup kullanıcı kapatana dek açık kalıyordu (türetme).*
 * Durum bu yüzden bir harita değil, TEK BİR ANAHTARDIR — "iki grup birden açık"
 * diye bir hâl tipte bile yazılamaz.
 *
 * Dört kural:
 *
 * 1. **Bir grup açılınca diğeri kapanır.**
 * 2. **Aktif sayfanın grubu açık başlar** — sayfa yüklenirken ve adres BAŞKA
 *    bir grubun sayfasına geçtiğinde. Depoda başka bir grup yazılı olsa da
 *    yüklemede aktif grup kazanır.
 * 3. **Kullanıcı açık grubu kapatabilir** (aktif grubu da) ve başka bir grubu
 *    açarak aktif grubu kapatabilir. Aynı grubun içinde gezerken grup yeniden
 *    AÇILMAZ — açılsaydı düğme *"bastım, olmadı"* derdi (§2.40). Kapalı grubun
 *    başlığı aktif sayfayı içeriyorsa VURGULUDUR (MG-7 — kabukta çizilir).
 * 4. **Açık grup hatırlanır** — tarayıcı deposunda (`localStorage`). Hatırlanan
 *    değer yalnız aktif grubu OLMAYAN sayfada (*Genel bakış* · menüsüz sayfa)
 *    yüklemede görünür; aktif grup varsa kural 2 kazanır. ⚠️ Sunucuda tercih
 *    tablosu AÇILMADI.
 *
 * ⚠️ **DEPO GÜVENİLMEZDİR:** gizli pencerede, site verisi engellenmişken ya da
 * kota doluyken erişim HATA FIRLATIR. Her okuma ve yazma korumalıdır; depo
 * yoksa menü varsayılanla çalışır ve yalnız hatırlamaz. Okunan değer
 * DOĞRULANIR: bilinen bir grup anahtarı değilse yok sayılır.
 */

export const MENU_DURUM_ANAHTARI = 'isg.menu.acik-grup';

type AcikGrup = MenuGrupAnahtari | null;

const grupAnahtariMi = (deger: unknown): deger is MenuGrupAnahtari =>
  MENU_GRUPLARI.some((grup) => grup.key === deger);

function depodanOku(): AcikGrup {
  try {
    const ham = localStorage.getItem(MENU_DURUM_ANAHTARI);
    return grupAnahtariMi(ham) ? ham : null;
  } catch {
    return null;
  }
}

function depoyaYaz(acik: AcikGrup): void {
  try {
    if (acik === null) localStorage.removeItem(MENU_DURUM_ANAHTARI);
    else localStorage.setItem(MENU_DURUM_ANAHTARI, acik);
  } catch {
    // Depo yok / dolu / engelli: menü çalışmaya devam eder, yalnız hatırlamaz.
  }
}

export interface MenuGrupDurumu {
  readonly acikMi: (grup: MenuGrupAnahtari) => boolean;
  readonly degistir: (grup: MenuGrupAnahtari) => void;
}

interface Durum {
  readonly acik: AcikGrup;
  /** Kural 2'nin son uygulandığı grup — değişince yeniden uygulanır. */
  readonly aktif: AcikGrup;
}

export function useMenuGrupDurumu(aktif: AcikGrup): MenuGrupDurumu {
  const [durum, durumuYaz] = useState<Durum>(() => ({ acik: aktif ?? depodanOku(), aktif }));

  // Adres başka bir grubun sayfasına geçti: o grup açılır (öncekisi kapanır).
  // Menüsüz bir sayfaya geçişte (`aktif === null`) açık grup OLDUĞU GİBİ kalır.
  // RENDER SIRASINDA uyarlanır (efektte değil) — efektte yapılsaydı yeni sayfa
  // bir kare boyunca eski grupla çizilir, *"buradasın"* işareti o karede
  // yanlış yerde dururdu.
  if (durum.aktif !== aktif) {
    durumuYaz({ acik: aktif ?? durum.acik, aktif });
  }

  useEffect(() => {
    depoyaYaz(durum.acik);
  }, [durum.acik]);

  const degistir = useCallback((grup: MenuGrupAnahtari): void => {
    durumuYaz((onceki) => ({ ...onceki, acik: onceki.acik === grup ? null : grup }));
  }, []);

  return { acikMi: (grup) => durum.acik === grup, degistir };
}
